<template>
  <Layout
    :assetType="assetType"
    :asset="asset"
    active="approval"
    :title="`Send for approval | ${assetType.name}`"
  >
    <div class="flex-grow-1 flex-column d-flex py-5 bg-light flex-shrink-0">
      <div class="flex-grow-1 flex-shrink-0">
        <div class="container">
          <div class="form-group">
            <Typeahead
              v-model="approver"
              label="Select Approvers"
              helper="Select the approver below that you would like to send the contract to"
              :items="approvers"
              :errors="$page.props.errors.approver"
              placeholder="Start typing to search..."
              :tolerance="0"
              return-field="id"
              id-field="id"
              search-field="fullname"
              display-field="fullname"
              @selected="approverSelected"
              @noResults="showAddNewApprover"
              @listAppend="showAddNewApprover"
              show-list-append
            >
              <template v-slot:item="slotProps">
                <span v-if="slotProps.match.fullname">
                  {{ slotProps.match.fullname }}
                  <br />
                </span>
                <small v-if="slotProps.match.email">Email: {{ slotProps.match.email }}</small>
              </template>
              <template v-slot:noResults>
                <small>We couldn't find a match.</small>
                <br />
                <span class="text-primary">Add a new Approver</span>
              </template>
              <template v-slot:listAppend>
                <small>Can't find the correct Approver?</small>
                <br />
                <span class="text-primary">Add a new Approver</span>
              </template>
            </Typeahead>
          </div>

          <div v-if="showAdd" class="card mb-4">
            <div class="card-body">
              <div class="form-group">
                <label class="form-label">Add New Approver</label>
                <small class="form-text form-helper">Enter the details below to add a new approver to your organisation.</small>
                <small class="form-text form-helper text-danger">
                  <p><b>Warning:</b> Sending this document to anyone outside of your organisation will enable that individual to access all documents for all of your Counterparties.</p>
                  <p>We recommend you only permit trusted advisers to have this access.</p>
                </small>

                <form @submit.prevent="addUser">
                  <div class="row">
                    <div class="form-group col-sm-6">
                      <TextInput
                        label="First Name"
                        v-model="user.first_name"
                        :errors="errors.first_name"
                        type="text"
                        autofocus
                        autocapitalize="off"
                        placeholder="Jane"
                        @input="errors.first_name = []"
                        validateSuccess
                        validateError
                      />
                    </div>
                    <div class="form-group col-sm-6">
                      <TextInput
                        label="Last Name"
                        v-model="user.last_name"
                        :errors="errors.last_name"
                        type="text"
                        autocapitalize="off"
                        placeholder="Doe"
                        @input="errors.last_name = []"
                        validateSuccess
                        validateError
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <TextInput
                      label="Email"
                      v-model="user.email"
                      :errors="errors.email"
                      type="email"
                      autocapitalize="off"
                      placeholder="jane.doe@example.com"
                      @input="errors.email = []"
                      validateSuccess
                      validateError
                    />
                  </div>

                  <div class="form-group">
                    <div class="row">
                      <div class="col-md-3">
                        <LoadingButton :loading="adding" class="btn btn-primary btn-block" type="submit">Create</LoadingButton>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <Message v-model="form.approver_message" :name="form.approver_name" />

          <CcEmails
            :reviewerCC="form.approver_cc"
            @added="(value) => form.approver_cc.push(value)"
            @removed="(index) => form.approver_cc.splice(index, 1)"
          />
        </div>
      </div>

      <form @submit.prevent="submit">
        <div class="mt-4 flex-shrink-0">
          <div class="container">
            <div class="row">
              <div class="col-md-4 offset-md-4">
                <LoadingButton :loading="sending" class="btn btn-block btn-primary" type="submit">
                  Send to Approver{{ form.approver_cc.length > 0 ? "s" : "" }}
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </Layout>
</template>

<script>
import Layout from './Layout';

import LoadingButton from '@/Shared/LoadingButton';
import TextInput from '@/Shared/Inputs/Text';
import Typeahead from '@/Shared/Inputs/NewTypeahead';
import CcEmails from '@/Shared/Inputs/CcEmails';
import Message from '@/Shared/Inputs/Message';
import helpers from '@/Mixins/helpers';

export default {
  mixins: [helpers],

  components: {
    Layout,
    LoadingButton,
    Typeahead,
    TextInput,
    CcEmails,
    Message,
  },

  props: {
    assetType: Object,
    asset: Object,
    approvers: Array,
    roles: Array,
  },

  data() {
    const approverData = this.asset && this.asset.approver && this.asset.approver[this.asset.is_with] ? this.asset.approver[this.asset.is_with] : [];

    return {
      form: this.$inertia.form({
        approver: approverData.approver ? approverData.approver : '',
        approver_name: approverData.approver_name ? approverData.approver_name : '',
        approver_message: approverData.approver_message ? approverData.approver_message : '',
        approver_cc: approverData.approver_cc ? approverData.approver_cc : [],
      }),
      approver: approverData.approver ? this.approvers.find((approver) => String(approver.id) === String(approverData.approver)) : null,
      user: {
        first_name: '',
        last_name: '',
        email: '',
        roles: [],
      },
      valid: false,
      adding: false,
      showAdd: false,
      sending: false,
      errors: {},
    };
  },

  mounted() {
    const approver = this.roles.find((role) => role.name === 'approver');
    this.user.roles.push(approver.id);
  },

  computed: {
    state() {
      return this.$store.state.assetState;
    },
  },

  methods: {
    showAddNewApprover() {
      this.showAdd = true;
    },

    approverSelected(cp) {
      this.$page.props.errors.approvers = null;

      if (cp) {
        this.form.approver = cp.id;
        this.form.approver_name = cp.fullname;
        this.showAdd = false;
      } else {
        this.form.approver = '';
      }
    },

    submit() {
      this.sending = true;

      this.form.put(this.route('asset.approval.update', this.asset.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.sending = false;
        },
      });
    },

    addUser() {
      this.adding = true;
      window.axios
        .post('/user', this.user)
        .then(({ data }) => {
          this.adding = false;
          this.showAdd = false;
          this.user.first_name = '';
          this.user.last_name = '';
          this.user.email = '';
          this.approver = data;
          this.form.approver = data.id;
        })
        .catch((error) => {
          this.adding = false;
          this.errors = error.response.data.errors;
          throw new Error(error);
        });
    },
  },
};
</script>
